import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePublicPetitionWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { PublicPetitionWizardDraft } from '../../../types/publicPetitionCaseRequest';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { PublicPetitionWizardActions } from './PublicPetitionWizardActions';
import {
  publicPetitionWizardPath,
  PublicPetitionWizardShell,
  FieldError,
  inpClass } from './publicPetitionWizardShell';
import { CONTACT_NUMBER_LENGTH, contactNumberError } from '../../../utils/caseFieldValidation';
import { onlyDigitsMax, MAX_NUMBER_OF_RESPONDENTS_DIGITS } from './publicPetitionHelpers';
import { validatePublicPetitionStep } from './publicPetitionStepValidation';
import { usePublicPetitionLookups } from './usePublicPetitionLookups';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_DESC = 2000;
const MAX_REF = 255;
const MAX_CONTACT = CONTACT_NUMBER_LENGTH;

const toOpts = (items: { id: string; name: string }[]) => items.map((x) => ({ id: x.id, label: x.name }));

function lookupName(items: { id: string; name: string }[], id: string): string {
  return items.find((x) => x.id === id)?.name ?? '';
}

function mergeLookupItems(
  items: { id: string; name: string }[],
  selectedId: string,
  selectedName: string,
): { id: string; name: string }[] {
  if (!selectedId || items.some((x) => x.id === selectedId)) return items;
  return [...items, { id: selectedId, name: selectedName || selectedId }];
}

export default function PublicPetitionStep2PartiesInvolvedPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = usePublicPetitionWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const { respondentTypes, loading } = usePublicPetitionLookups('step2');

  const respondentTypeOptions = useMemo(
    () => toOpts(mergeLookupItems(respondentTypes, draft.respondentTypeId, draft.respondentTypeName)),
    [draft.respondentTypeId, draft.respondentTypeName, respondentTypes],
  );

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validatePublicPetitionStep(2, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const next = () => {
    if (!validate()) return;
    nav(publicPetitionWizardPath(3, draft.draftRequestId));
  };

  return (
    <PublicPetitionWizardShell currentStep={2} draft={draft}>
      <SectionCard title={f.sectionPetitionerDetails}>
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              Name <span className="req">*</span>
            </label>
            <input
              id="pp-petitionerName"
              className={inpClass(Boolean(errors.petitionerName))}
              type="text"
              value={draft.petitionerName}
              placeholder={f.enterPetitionerName}
              onChange={(e) => {
                setDraft((d: PublicPetitionWizardDraft) => ({
                  ...d,
                  petitionerName: maxLen('petitionerName', e.target.value, MAX_REF) }));
              }}
            />
            <FieldError message={errors.petitionerName} />
          </div>
          <div className="ccr-fg">
            <label>{f.nicIdentificationNumber}</label>
            <input
              id="pp-nic"
              className={inpClass(Boolean(errors.nicOrIdentificationNumber))}
              type="text"
              value={draft.nicOrIdentificationNumber}
              placeholder={f.enterNicOrPassport}
              onChange={(e) => {
                setDraft((d: PublicPetitionWizardDraft) => ({
                  ...d,
                  nicOrIdentificationNumber: maxLen('nicOrIdentificationNumber', e.target.value, MAX_REF) }));
              }}
            />
            <FieldError message={errors.nicOrIdentificationNumber} />
          </div>
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.address} <span className="req">*</span>
          </label>
          <textarea
            id="pp-petitionerAddress"
            className={inpClass(Boolean(errors.petitionerAddress))}
            rows={3}
            value={draft.petitionerAddress}
            placeholder={f.enterAddress}
            onChange={(e) => {
              setDraft((d: PublicPetitionWizardDraft) => ({
                ...d,
                petitionerAddress: maxLen('petitionerAddress', e.target.value, MAX_DESC) }));
            }}
          />
          <FieldError message={errors.petitionerAddress} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.contactNumber}</label>
            <input
              id="pp-petitionerContact"
              className={inpClass(Boolean(errors.petitionerContactNumber))}
              type="text"
              inputMode="numeric"
              value={draft.petitionerContactNumber}
              placeholder={f.contactNumberExample}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT);
                setDraft((d: PublicPetitionWizardDraft) => ({ ...d, petitionerContactNumber: digits }));
                const err = contactNumberError(digits);
                setErrors((prev) => ({ ...prev, petitionerContactNumber: err ?? '' }));
              }}
            />
            <FieldError message={errors.petitionerContactNumber} />
          </div>
          <div className="ccr-fg">
            <label>{f.email}</label>
            <input
              id="pp-petitionerEmail"
              className={inpClass(Boolean(errors.petitionerEmail))}
              type="email"
              value={draft.petitionerEmail}
              placeholder={f.enterEmailAddress}
              onChange={(e) => {
                setDraft((d: PublicPetitionWizardDraft) => ({
                  ...d,
                  petitionerEmail: maxLen('petitionerEmail', e.target.value, MAX_REF) }));
              }}
            />
            <FieldError message={errors.petitionerEmail} />
          </div>
        </div>

        <div className="ccr-fg" id="pp-representedByAttorney" style={{ marginBottom: 14 }}>
          <label>{f.representedByAttorney}</label>
          <div className="ccr-radio-row">
            {(['yes', 'no'] as const).map((v) => (
              <label key={v} className="ccr-radio">
                <input
                  type="radio"
                  name="pp-representedByAttorney"
                  checked={v === 'yes' ? draft.representedByAttorney : !draft.representedByAttorney}
                  onChange={() => {
                    const yes = v === 'yes';
                    setDraft((d: PublicPetitionWizardDraft) => ({
                      ...d,
                      representedByAttorney: yes,
                      ...(!yes ? { attorneyName: '', attorneyContactNumber: '' } : {}),
                    }));
                    clearErr('attorneyName');
                    clearErr('attorneyContactNumber');
                  }}
                />
                {v === 'yes' ? f.yes : f.no}
              </label>
            ))}
          </div>
        </div>

        {draft.representedByAttorney ? (
          <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
            <div className="ccr-fg">
              <label>{f.attorneyName}</label>
              <input
                id="pp-attorneyName"
                className={inpClass(Boolean(errors.attorneyName))}
                type="text"
                value={draft.attorneyName}
                placeholder={f.enterAttorneyName}
                onChange={(e) => {
                  setDraft((d: PublicPetitionWizardDraft) => ({
                    ...d,
                    attorneyName: maxLen('attorneyName', e.target.value, MAX_REF) }));
                }}
              />
              <FieldError message={errors.attorneyName} />
            </div>
            <div className="ccr-fg">
              <label>{f.attorneyContactNumber}</label>
              <input
                id="pp-attorneyContact"
                className={inpClass(Boolean(errors.attorneyContactNumber))}
                type="text"
                inputMode="numeric"
                value={draft.attorneyContactNumber}
                placeholder={f.contactNumberExample}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT);
                  setDraft((d: PublicPetitionWizardDraft) => ({ ...d, attorneyContactNumber: digits }));
                  const err = contactNumberError(digits);
                  setErrors((prev) => ({ ...prev, attorneyContactNumber: err ?? '' }));
                }}
              />
              <FieldError message={errors.attorneyContactNumber} />
            </div>
          </div>
        ) : null}
      </SectionCard>

      <SectionCard title={f.sectionRespondentDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.respondentNames} <span className="req">*</span>
          </label>
          <textarea
            id="pp-respondentNames"
            className={inpClass(Boolean(errors.respondentNames))}
            rows={3}
            value={draft.respondentNames}
            onChange={(e) => {
              setDraft((d: PublicPetitionWizardDraft) => ({
                ...d,
                respondentNames: maxLen('respondentNames', e.target.value, MAX_DESC) }));
            }}
            placeholder={f.enterRespondentNames}
          />
          <FieldError message={errors.respondentNames} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.respondentType}</label>
            <WizardSelect
              id="pp-respondentType"
              hasError={Boolean(errors.respondentTypeId)}
              value={draft.respondentTypeId}
              disabled={loading}
              placeholder={loading ? f.loading : f.selectRespondentType}
              options={respondentTypeOptions}
              onChange={(id) => {
                const name = respondentTypeOptions.find((o) => o.id === id)?.label ?? lookupName(respondentTypes, id);
                setDraft((d: PublicPetitionWizardDraft) => ({
                  ...d,
                  respondentTypeId: id,
                  respondentTypeName: name }));
                clearErr('respondentTypeId');
              }}
            />
            <FieldError message={errors.respondentTypeId} />
          </div>
          <div className="ccr-fg">
            <label>{f.numberOfRespondents}</label>
            <input
              id="pp-numberOfRespondents"
              className={inpClass(Boolean(errors.numberOfRespondents))}
              type="text"
              inputMode="numeric"
              pattern="\\d*"
              value={draft.numberOfRespondents}
              placeholder={f.enterNumberOfRespondents}
              onChange={(e) => {
                setDraft((d: PublicPetitionWizardDraft) => ({
                  ...d,
                  numberOfRespondents: onlyDigitsMax(e.target.value, MAX_NUMBER_OF_RESPONDENTS_DIGITS) }));
                clearErr('numberOfRespondents');
              }}
            />
            <FieldError message={errors.numberOfRespondents} />
          </div>
        </div>

        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>{f.respondentAddress}</label>
          <textarea
            id="pp-respondentAddress"
            className={inpClass(Boolean(errors.respondentAddress))}
            rows={3}
            value={draft.respondentAddress}
            placeholder={f.enterRespondentAddress}
            onChange={(e) => {
              setDraft((d: PublicPetitionWizardDraft) => ({
                ...d,
                respondentAddress: maxLen('respondentAddress', e.target.value, MAX_DESC) }));
            }}
          />
          <FieldError message={errors.respondentAddress} />
        </div>
      </SectionCard>

      <PublicPetitionWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={2}
        onBack={() => nav(publicPetitionWizardPath(1, draft.draftRequestId))}
        onNext={next}
        validateBeforeSave={validate}
      />
    </PublicPetitionWizardShell>
  );
}
